import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
    allAgencyJurisdictionsSelector,
    selectedJurisdictionSelector,
} from '../../../../store/user-info/user-info.selectors';
import { setSelectedJurisdictionOperation } from '../../../../store/user-info/user-info.operations';
import { setUserAgencyIdOperation } from '../../../../store/user/user.operations';
import { userEffectiveAgencyIdSelector } from '../../../../store/user/user.selectors';
import {
    getAgencyRoutesOperation,
    getRoutesStopsOperation,
} from '../../../../routes/routes/routes.operations';
import LytDropdown from '../../../library/dropdown/LytDropdown';
import { ICON_NAMES } from '../../../library/icon/icon-constants';
const SelectorContainer = styled.div`
    padding: 8px 16px;
    position: relative;
`;
const DropdownWrapper = styled.div`
    width: 100%;
`;
const CollapsedContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 0;
`;
const SelectorLabel = styled.div`
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.components.sidebar.section_title.color};
    text-transform: uppercase;
    padding: 0 0 4px 0;
    letter-spacing: 0.5px;
`;

/**
 * LytJurisdictionSelector - Jurisdiction selector for traffic viewer users
 *
 * This component allows traffic viewers to select their active jurisdiction.
 * The selection is persisted in Redux for use across the application.
 *
 * @param {Object} props
 * @param {boolean} props.collapsed - Whether the sidebar is collapsed
 */
const LytJurisdictionSelector = ({ collapsed = false }) => {
    const dispatch = useDispatch();
    const { list, loading } = useSelector(allAgencyJurisdictionsSelector);
    const selectedJurisdictionId = useSelector(selectedJurisdictionSelector);
    const currentAgencyId = useSelector(userEffectiveAgencyIdSelector);

    // Auto-select first jurisdiction if none selected and list is available
    useEffect(() => {
        if (!selectedJurisdictionId && list.length > 0) {
            const firstJurisdiction = list[0];
            dispatch(setSelectedJurisdictionOperation(firstJurisdiction.id));

            // Also set the effective agency to match
            if (
                firstJurisdiction.agencyId &&
                firstJurisdiction.agencyId !== currentAgencyId
            ) {
                dispatch(setUserAgencyIdOperation(firstJurisdiction.agencyId));
            }
        }
    }, [selectedJurisdictionId, list, currentAgencyId, dispatch]);
    const handleJurisdictionSelect = useCallback(
        (jurisdictionId) => {
            if (jurisdictionId !== selectedJurisdictionId) {
                dispatch(setSelectedJurisdictionOperation(jurisdictionId));

                // Update the cross-application effective agency when the jurisdiction
                // belongs to a different agency than the currently selected one.
                const jurisdiction = list.find((j) => j.id === jurisdictionId);
                if (
                    jurisdiction?.agencyId &&
                    jurisdiction.agencyId !== currentAgencyId
                ) {
                    dispatch(setUserAgencyIdOperation(jurisdiction.agencyId));
                    dispatch(getAgencyRoutesOperation());
                    dispatch(getRoutesStopsOperation({}));
                }
            }
        },
        [dispatch, selectedJurisdictionId, currentAgencyId, list]
    );

    // Check if jurisdictions span multiple agencies
    const hasMultipleAgencies = useMemo(() => {
        const uniqueGroups = new Set(list.map((j) => j.group).filter(Boolean));
        return uniqueGroups.size > 1;
    }, [list]);
    const dropdownData = useMemo(() => {
        const mapped = list.map(({ id, name, group }) => ({
            id,
            text: name,
            // Only include group field when there are multiple agencies
            ...(hasMultipleAgencies && group
                ? {
                      group,
                  }
                : {}),
        }));
        // When grouped, sort within each group (options already arrive grouped by agency).
        // When ungrouped, sort alphabetically.
        if (!hasMultipleAgencies) {
            return mapped.sort((a, b) => a.text.localeCompare(b.text));
        }
        return mapped;
    }, [list, hasMultipleAgencies]);
    const selectedJurisdictionName = useMemo(() => {
        if (loading) return 'Loading...';
        const jurisdiction = list.find((j) => j.id === selectedJurisdictionId);
        if (!jurisdiction) return 'Select Jurisdiction';
        return jurisdiction.group
            ? `${jurisdiction.group}: ${jurisdiction.name}`
            : jurisdiction.name;
    }, [list, selectedJurisdictionId, loading]);

    // Handle dropdown change from LytDropdown
    const handleDropdownChange = useCallback(
        (selectedOptions) => {
            const selectedId = Object.keys(selectedOptions).find(
                (key) => selectedOptions[key]
            );
            if (selectedId) {
                handleJurisdictionSelect(selectedId);
            }
        },
        [handleJurisdictionSelect]
    );

    // Don't render if there are no jurisdictions
    if (!loading && list.length === 0) {
        return null;
    }
    if (collapsed) {
        return (
            <CollapsedContainer>
                <LytDropdown
                    options={dropdownData}
                    selectedOptions={{
                        [selectedJurisdictionId]: true,
                    }}
                    onChange={handleDropdownChange}
                    dropdownPlaceholder="Select Jurisdiction"
                    triggerText={selectedJurisdictionName}
                    loading={loading}
                    triggerIcon={ICON_NAMES.VerticalDots}
                    triggerType="secondary"
                    usePortal
                    menuHeader={
                        hasMultipleAgencies
                            ? 'You are currently registered in the following jurisdictions'
                            : undefined
                    }
                />
            </CollapsedContainer>
        );
    }

    // Expanded state - use LytDropdown with label
    return (
        <SelectorContainer>
            <DropdownWrapper>
                <LytDropdown
                    options={dropdownData}
                    selectedOptions={{
                        [selectedJurisdictionId]: true,
                    }}
                    onChange={handleDropdownChange}
                    dropdownPlaceholder="Select Jurisdiction"
                    triggerText={selectedJurisdictionName}
                    loading={loading}
                    fullWidth
                    menuHeader={
                        hasMultipleAgencies
                            ? 'You are currently registered in the following jurisdictions'
                            : undefined
                    }
                />
            </DropdownWrapper>
        </SelectorContainer>
    );
};
LytJurisdictionSelector.propTypes = {
    collapsed: PropTypes.bool,
};
export default LytJurisdictionSelector;
